import { Box, Typography, Divider, useTheme } from '@mui/material';
import SectionContent from '../../components/SectionContent';
import ModernList from '../../components/ModernList';

const contactInfo = [
  { label: 'Company', value: 'Spinnaker Algo Limited' },
  { label: 'Address', value: 'Level 30, The Leadenhall Building, 122 Leadenhall Street, London EC3V 4AB UK' },
  { label: 'Email', value: 'compliance@spinnakeralgo.co.uk', type: 'email' },
  { label: 'Phone', value: '+44 20 4587 3860', type: 'phone' },
];

const CookiePolicyPage = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '80vh',
        bgcolor: 'background.default',
        py: { xs: 2, md: 3 }, // Decreased margin
      }}
    >
      <SectionContent
        variant="main"
        heading={null}
        headingLevel={3}
        spacing={2.5}
        align="left"
        sx={{ maxWidth: 900, mt: 1, px: { xs: 2, sm: 4, md: 6 }, pt: 2, pb: 2 }} // Wider card, more padding
      >
        <Box sx={{ px: 2, py: 2 }}>
          <Typography
            variant="h3"
            sx={{ color: theme.palette.text.primary, fontWeight: 700, textAlign: 'left', fontSize: { xs: '2rem', sm: '2.3rem' }, mb: 0.5 }}
          >
            Cookie Policy
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left', fontSize: { xs: '1.1rem', sm: '1.2rem' } }}
          >
            Last updated: May 1, 2025
          </Typography>
        </Box>
        <Divider sx={{ mb: 2, mx: 0, bgcolor: theme.palette.grey[200], borderRadius: 2 }} />
        <SectionContent variant="sub" spacing={1.5} align="left">
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, textAlign: 'left' }}>
            At Spinnaker Algo, we respect your privacy and are committed to protecting your personal information. This Cookie Policy explains how we use cookies on our website, the types of cookies we use, and how you can manage your cookie preferences.
          </Typography>
        </SectionContent>
        <SectionContent variant="sub" heading="What are cookies?" headingLevel={5} spacing={1.5} align="left">
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
            Cookies are small text files that are stored on your device (computer, tablet, smartphone) when you visit a website. Cookies allow the website to remember your preferences, past activities, and customize your browsing experience.
          </Typography>
        </SectionContent>
        <SectionContent variant="sub" heading="Types of cookies we use" headingLevel={5} spacing={1.5} align="left">
          <ModernList
            items={[
              {
                text: 'Strictly Necessary Cookies',
                icon: <Box sx={{ bgcolor: '#e9ecef', borderRadius: '50%', width: 16, height: 16, display: 'inline-block', mr: 1, border: '1.5px solid #6511af' }} />,
                children: [
                  'Essential for basic functionality, such as accessing secure areas and submitting forms. Without these cookies, some services may not be available.'
                ]
              },
              {
                text: 'Performance Cookies',
                icon: <Box sx={{ bgcolor: '#e9ecef', borderRadius: '50%', width: 16, height: 16, display: 'inline-block', mr: 1, border: '1.5px solid #6511af' }} />,
                children: [
                  'Collect information about how visitors use our website, such as the most visited pages and any error messages. These cookies do not collect personally identifiable information and are used exclusively to improve the performance of the website.'
                ]
              },
              {
                text: 'Functionality Cookies',
                icon: <Box sx={{ bgcolor: '#e9ecef', borderRadius: '50%', width: 16, height: 16, display: 'inline-block', mr: 1, border: '1.5px solid #6511af' }} />,
                children: [
                  'Allow the website to remember your choices, such as your username, language, or location, and provide enhanced, personalized features.'
                ]
              },
              {
                text: 'Advertising and Marketing Cookies',
                icon: <Box sx={{ bgcolor: '#e9ecef', borderRadius: '50%', width: 16, height: 16, display: 'inline-block', mr: 1, border: '1.5px solid #6511af' }} />,
                children: [
                  'Used to display personalized ads based on your interests. Also used to limit the number of times you see an advertisement and to measure the effectiveness of advertising campaigns.'
                ]
              },
              {
                text: 'Third-Party Cookies',
                icon: <Box sx={{ bgcolor: '#e9ecef', borderRadius: '50%', width: 16, height: 16, display: 'inline-block', mr: 1, border: '1.5px solid #6511af' }} />,
                children: [
                  'We may also use third-party cookies, such as social media and analytics tools (e.g., Google Analytics), to gather information about user behavior and improve our services. These cookies may collect data about your browsing across multiple websites.'
                ]
              }
            ]}
            type="icon"
            spacing={3}
            align="left"
            heading={null}
            nested={true}
          />
        </SectionContent>
        <SectionContent variant="sub" heading="How we use cookies" headingLevel={5} spacing={1.5} align="left">
          <Box sx={{ pl: 2, pr: 2, py: 1 }}>
            <ul style={{ margin: 0, paddingLeft: '1.2em', fontSize: '1.15rem', color: theme.palette.text.primary }}>
              <li>To enhance the user experience on our website.</li>
              <li>To personalize content and ads.</li>
              <li>To monitor website usage and performance.</li>
              <li>To conduct data analysis to improve our services.</li>
              <li>To engage in marketing and advertising activities.</li>
            </ul>
          </Box>
        </SectionContent>
        <SectionContent variant="sub" heading="Managing cookies" headingLevel={5} spacing={1.5} align="left">
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
            You have the right to control and manage the cookies stored on your device. You can do this in several ways:
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600, color: theme.palette.text.primary, textAlign: 'left' }}>Browser Settings</Typography>
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'left', color: theme.palette.text.secondary }}>
            Most browsers allow you to refuse or accept cookies. You can configure your browser to notify you whenever a cookie is sent or block specific cookies. If you block cookies, some features of the website may not work properly.
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600, color: theme.palette.text.primary, textAlign: 'left' }}>Cookie Settings on Our Website</Typography>
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'left', color: theme.palette.text.secondary }}>
            When you first visit our website, a notification will ask for your consent to use cookies. You can accept or reject cookies, as well as customize your preferences through the cookie bar or on the cookie settings page.
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600, color: theme.palette.text.primary, textAlign: 'left' }}>Third-Party Tools</Typography>
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'left', color: theme.palette.text.secondary }}>
            Some cookies, such as advertising cookies, can be controlled through third-party tools. You can access these tools to manage your cookie preferences.
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600, color: theme.palette.text.primary, textAlign: 'left' }}>Third-Party Cookies</Typography>
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'left', color: theme.palette.text.secondary }}>
            We may use third-party services, such as Google Analytics, to analyze user behavior on our website. These services may use cookies to collect information about your visit and help improve the functionality and content of our website.<br />
            For more information about Google Analytics cookies, you can review the Google Privacy Policy.
          </Typography>
        </SectionContent>
        <SectionContent variant="sub" heading="Data Retention" headingLevel={5} spacing={1.5} align="left">
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
            The cookies we use are stored on your device for as long as necessary to fulfill the purposes for which they were set, after which they will be automatically deleted, except for persistent cookies, which remain stored until you delete them.
          </Typography>
        </SectionContent>
        <SectionContent variant="sub" heading="Changes to this Cookie Policy" headingLevel={5} spacing={1.5} align="left">
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
            We may update this Cookie Policy from time to time to reflect changes in our services or applicable laws. When this occurs, we will post the new version on our website and indicate the date of the last update. We encourage you to review this policy regularly to stay informed about our use of cookies.
          </Typography>
        </SectionContent>
        <SectionContent variant="sub" heading="Contact Us" headingLevel={5} spacing={1.5} align="left">
          <Box sx={{ mb: 2, mt: 1 }}>
            {contactInfo.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ mb: 0.5, textAlign: 'left', color: theme.palette.text.secondary }}>
                <b>{item.label}:</b> {item.type === 'email' ? (
                  <a href={`mailto:${item.value}`} style={{ color: theme.palette.primary.main, fontWeight: 600 }}>{item.value}</a>
                ) : item.type === 'phone' ? (
                  <a href={`tel:${item.value}`} style={{ color: theme.palette.primary.main, fontWeight: 600 }}>{item.value}</a>
                ) : item.value}
              </Typography>
            ))}
          </Box>
        </SectionContent>
              <Divider sx={{ mb: 2, mx: 0, bgcolor: theme.palette.grey[200], borderRadius: 2 }} />
        <SectionContent variant="footer" spacing={1} align="left" sx={{ boxShadow: 'none', bgcolor: 'transparent', borderRadius: 0, px: 2, py: 0 }}>
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.secondary, textAlign: 'left', background: 'none', boxShadow: 'none', borderRadius: 0, px: 0, py: 0 }}>
            This Cookie Policy ensures that users have a clear and detailed understanding of how cookies are used on our site and provides them with the ability to manage their preferences transparently.<br />
            Your privacy is important to us. We are committed to being transparent about our use of cookies.
          </Typography>
        </SectionContent>
      </SectionContent>
    </Box>
  );
}
export default CookiePolicyPage;